<script setup>
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type } = defineProps({
    type: {
        type: String,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'ticketsCreated',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Tickets',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.ticketsCreated,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'emailings',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Emailings',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.emailings,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'emailsSent',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Emails envoyés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.emailsSent,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'revenue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA TTC',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.revenue,
                    withDecimals: false,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'revenueFromOpenedEmail',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA TTC<br/>après ouverture',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.revenueFromOpenedEmail,
                    withDecimals: false,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'revenueFromClickedEmail',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA TTC<br/>après clic',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.revenueFromClickedEmail,
                    withDecimals: false,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'price',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix',
                }),
            cell: (props) =>
                props.row.original.price
                    ? h(PriceCell, { price: props.row.original.price })
                    : props.row.original.modulePackage
                      ? h(PriceCell, {
                            price: props.row.original.modulePackage.price,
                        })
                      : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'minimumSentAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: '1er email envoyé le',
                }),
            cell: (props) =>
                props.row.original.minimumSentAt
                    ? h(TextCell, {
                          text: props.row.original.minimumSentAt,
                      })
                    : null,
            enableSorting: false,
        }),
    ],
    url: route('front.projectManagement.modules.postModuleInvoicing'),
    data: {
        type,
    },
    columnVisibility: {
        emailings: type === 'emailing',
        emailsSent: type === 'emailing' || type === 'emailing_automation',
        revenue: type === 'emailing' || type === 'emailing_automation',
        revenueFromOpenedEmail:
            type === 'emailing' || type === 'emailing_automation',
        revenueFromClickedEmail:
            type === 'emailing' || type === 'emailing_automation',
        minimumSentAt: type === 'emailing_automation',
        ticketsCreated: type === 'customer_ticket',
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
